// The table: draws a state, takes the player's hands, and plays out what
// the engine says happened, one step at a time.
//
// Cards are absolutely placed elements, one per card id, kept between
// states so they can glide. Every position comes from layoutOf(start, state)
// (src/ui/geometry.ts), so a redraw after resize or undo is the same code as
// a move. The look is in static/css/ternion.css; timings in config/motion.ts.

import { ICONS } from "../config/icons.ts"
import { LAYOUT } from "../config/layout.ts"
import { MOTION } from "../config/motion.ts"
import { html, joinHtml, EMPTY_HTML, unsafeRawHtml, type Html } from "../boundary/html.ts"
import {
  restRing,
  runLength,
  seen,
  type Card,
  type CardId,
  type FoldEvent,
  type From,
  type GameEvent,
  type Move,
  type MoveEvent,
  type Refusal,
  type TableState,
  type To,
  type TurnEvent,
} from "../domain/engine.ts"
import { reversed } from "../domain/arrays.ts"
import { keyOf, ringsOf, scaleOf, valueOf, type GlyphKey, type RingIndex, type Rings } from "../domain/rings.ts"
import type { Clock } from "../effects/clock.ts"
import type { Frames, Motion } from "../effects/motion.ts"
import type { Sound } from "../effects/sound.ts"
import { closest, numberData, part, parts, replay, setHtml } from "./dom.ts"
import { altarX, colX, layoutOf, placesOf, type Geo, type Place, type Point } from "./geometry.ts"
import { indexRow, realmOf, sigil } from "./glyph.ts"

export type TableDeps = {
  readonly sound: Sound
  readonly motion: Motion
  readonly clock: Clock
  // try a move; true when the engine took it
  readonly attempt: (move: Move, ids: readonly CardId[]) => Promise<boolean>
}

export type ShowMode = "glide" | "instant" | "repaint"

export type TableView = {
  // `start` is the rite's first table: it keeps the table in one place
  readonly show: (start: TableState, state: TableState, mode: ShowMode) => void
  readonly play: (events: readonly GameEvent[]) => Promise<void>
  readonly refuse: (move: Move, refusal: Refusal, ids: readonly CardId[]) => void
  readonly glimmer: (ids: readonly CardId[]) => void
  readonly ghost: (move: Move) => Promise<void>
  readonly liftedIds: (move: Move) => readonly CardId[]
  readonly setRosetta: (on: boolean) => void
  readonly clearSelection: () => void
  readonly key: (key: string) => boolean
  readonly busy: () => boolean
}

type Selection = { readonly from: From; readonly ids: readonly CardId[] }
type Grasp = Selection & { readonly liftable: boolean }
// `starts`: where each lifted card was drawn when the player pressed.
type Drag = Grasp & {
  readonly starts: ReadonlyMap<CardId, Point>
  readonly x0: number
  readonly y0: number
  readonly pointer: number
  readonly moved: boolean
}
type Focus = { readonly row: "cols" | "altars"; readonly i: number }
type Glide = { readonly delay: number; readonly flip: boolean; readonly rings: Rings; readonly card: Card }

const markSvg = (svg: string): Html => unsafeRawHtml(`<svg viewBox="0 0 24 24" aria-hidden="true">${svg}</svg>`)
const translate = (p: Point): string => `translate(${p.x}px, ${p.y}px)`
const ROSETTA_MARK = { up: "▲", level: "·", down: "▼", folded: "◆" } as const

const rosettaMarks = (rings: Rings): string =>
  reversed(rings)
    .map((r) => (r === "folded" ? ROSETTA_MARK.folded : r === 1 ? ROSETTA_MARK.up : r === -1 ? ROSETTA_MARK.down : ROSETTA_MARK.level))
    .join("")

const rosettaHtml = (rings: Rings): Html => {
  const n = valueOf(rings)
  return html`<span class="ros"><b>${rosettaMarks(rings)}</b><i>${scaleOf(rings) > 1 ? "≈" : ""}${n > 0 ? "+" : ""}${n}</i></span>`
}

export const createTable = (root: HTMLElement, deps: TableDeps): TableView => {
  const { sound, motion, clock } = deps
  const slots = part(root, ".slots")
  const layer = part(root, ".cards")
  const els = new Map<CardId, HTMLElement>()
  const pos = new Map<CardId, Place>()
  const drawn = new Map<CardId, GlyphKey>()
  const dragAt = new Map<CardId, Point>()
  let start: TableState | undefined
  let current: TableState | undefined
  let geo: Geo | undefined
  let rosetta = false
  let busy = false
  let drag: Drag | undefined
  let selection: Selection | undefined
  let focus: Focus | undefined

  const T = (ms: number): number => (motion.reduced ? Math.min(ms, MOTION.reducedCap) : ms)
  const wait = (ms: number): Promise<void> => new Promise((resolve) => clock.after(ms, resolve))
  const animate = (el: Element, frames: Frames, options: KeyframeAnimationOptions): Promise<void> =>
    motion.reduced ? Promise.resolve() : motion.animate(el, frames, options)
  const measure = (state: TableState): Geo => layoutOf(start ?? state, state, root.clientWidth, root.clientHeight)

  // ---------- drawing ----------

  const paint = (el: HTMLElement, rings: Rings, card: Card): void => {
    el.dataset["realm"] = realmOf(rings)
    el.dataset["scale"] = String(scaleOf(rings))
    setHtml(el, html`${indexRow(rings)}${sigil(rings)}${rosetta ? rosettaHtml(rings) : EMPTY_HTML}`)
    drawn.set(card.id, keyOf(rings))
  }

  const drawnRings = (id: CardId): Rings => ringsOf(drawn.get(id) ?? "0")

  const makeEl = (card: Card, rings: Rings): HTMLElement => {
    const el = document.createElement("div")
    el.className = "card"
    el.dataset["id"] = String(card.id)
    paint(el, rings, card)
    layer.append(el)
    els.set(card.id, el)
    return el
  }

  const put = (el: HTMLElement, p: Place): void => {
    el.style.transform = translate(p)
    el.style.zIndex = String(p.z)
  }

  const forget = (id: CardId): void => {
    els.delete(id)
    pos.delete(id)
    drawn.delete(id)
    dragAt.delete(id)
  }

  const columnMarks = (state: TableState, g: Geo, i: number): Html => {
    const col = state.cols[i]
    if (col === undefined) return EMPTY_HTML
    // a tide column that has turned over is a mirror too: both marks show
    const marks = [...(col.tide ? (["tide"] as const) : []), ...(col.mirror ? (["mirror"] as const) : [])]
    const M = LAYOUT.columnMark
    return joinHtml(
      marks.map((kind, k) => {
        const dx = (k - (marks.length - 1) / 2) * g.cw * M.spread
        const x = colX(g, i) + g.cw / 2 - g.cw * M.size + dx
        return html`<div class="col-mark ${kind}" data-col="${i}" style="transform:translate(${x}px,${g.colY - g.cw * M.rise}px)">${markSvg(ICONS[kind].svg)}</div>`
      }),
    )
  }

  const drawSlots = (state: TableState, g: Geo): void => {
    root.style.setProperty("--cw", `${g.cw}px`)
    root.classList.toggle("compact", g.compact)
    const altars = state.altars.map(
      (_, i) => html`<div class="slot altar" data-altar="${i}" style="transform:translate(${altarX(g, i)}px,${g.topY}px)"><span class="altar-eye"></span></div>`,
    )
    const cols = state.cols.map((col, i) => {
      const kind = `${col.mirror ? " mirror" : ""}${col.tide ? " tide" : ""}`
      return html`<div class="slot base${kind}" data-col="${i}" style="transform:translate(${colX(g, i)}px,${g.colY}px)"></div>${columnMarks(state, g, i)}<div class="lane${kind}" style="transform:translate(${colX(g, i)}px,${g.colY}px);height:${g.H - g.colY}px"></div>`
    })
    setHtml(slots, html`${altars}${cols}<div class="kb-ring" hidden></div>`)
    slots.style.setProperty("--ch", `${g.ch}px`)
    drawFocus()
  }

  // Cards in a liftable run get a quiet mark; the rest are dimmed a little.
  const markRuns = (state: TableState): void => {
    for (const col of state.cols) {
      const run = runLength(col)
      for (const [j, card] of col.cards.entries()) els.get(card.id)?.classList.toggle("buried", j < col.cards.length - run)
    }
    for (const a of state.altars) if (a !== "empty") els.get(a.id)?.classList.remove("buried")
  }

  // Draw a state. Cards that exist glide; new ones appear; gone ones fade.
  const show = (first: TableState, state: TableState, mode: ShowMode): void => {
    start = first
    current = state
    const g = measure(state)
    geo = g
    drawSlots(state, g)
    const places = placesOf(state, g)
    const instant = mode !== "glide" || motion.reduced
    for (const [id, el] of [...els]) {
      if (places.has(id)) continue
      forget(id)
      if (instant) el.remove()
      else void animate(el, [{ opacity: 1 }, { opacity: 0 }], { duration: MOTION.ms.fadeOut }).then(() => el.remove())
    }
    for (const [id, p] of places) {
      const existing = els.get(id)
      const el = existing ?? makeEl(p.card, p.rings)
      if (existing === undefined && !instant) void animate(el, [{ opacity: 0 }, { opacity: 1 }], { duration: MOTION.ms.fadeIn })
      if (existing !== undefined && (mode === "repaint" || drawn.get(id) !== keyOf(p.rings))) paint(el, p.rings, p.card)
      const old = pos.get(id)
      put(el, p)
      if (old !== undefined && !instant && (old.x !== p.x || old.y !== p.y)) {
        void animate(el, [{ transform: translate(old) }, { transform: translate(p) }], { duration: MOTION.ms.settle, easing: MOTION.easing.settle })
      }
      pos.set(id, p)
    }
    markRuns(state)
  }

  // ---------- playing out events ----------

  const glide = (el: HTMLElement, from: Point, to: Place, how: Glide | "plain"): Promise<void> => {
    const flip = how !== "plain" && how.flip
    if (motion.reduced) {
      if (how !== "plain" && flip) paint(el, how.rings, how.card)
      return Promise.resolve()
    }
    const delay = how === "plain" ? 0 : how.delay
    const a = translate(from)
    const b = translate(to)
    el.style.zIndex = String(500 + to.z)
    if (how === "plain" || !flip) {
      return motion
        .animate(el, [{ transform: a }, { transform: b }], { duration: MOTION.ms.glide, delay, easing: MOTION.easing.glide, fill: "backwards" })
        .then(() => {
          el.style.zIndex = String(to.z)
        })
    }
    // Turned over on the way: the glyph changes at the edge-on moment.
    const m = translate({ x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 - MOTION.flipArc })
    const half = { duration: MOTION.ms.glide * MOTION.flipShare, easing: MOTION.easing.flip }
    return motion
      .animate(el, [{ transform: `${a} rotateX(0deg)` }, { transform: `${m} rotateX(90deg)` }], { ...half, delay, fill: "backwards" })
      .then(() => {
        paint(el, how.rings, how.card)
        el.classList.add("turned")
        return motion.animate(el, [{ transform: `${m} rotateX(-90deg)` }, { transform: `${b} rotateX(0deg)` }], half)
      })
      .then(() => {
        el.style.zIndex = String(to.z)
        clock.after(400, () => el.classList.remove("turned"))
      })
  }

  // The ring that joined a landed card to the card under it lights up.
  const glowJoin = (state: TableState, e: MoveEvent, i: number): void => {
    const col = state.cols[i]
    if (col === undefined) return
    const first = col.cards.findIndex((c) => c.id === e.ids[0])
    const card = col.cards[first]
    const below = col.cards[first - 1]
    if (card === undefined || below === undefined) return
    const ring = restRing(seen(card, col.mirror), seen(below, col.mirror))
    if (ring === undefined) return
    for (const id of [card.id, below.id]) flashRing(id, ring)
  }

  const flashRing = (id: CardId, ring: RingIndex): void => {
    for (const g of parts(els.get(id) ?? layer, `[data-ring="${ring}"]`)) {
      replay(g, "join")
      clock.after(MOTION.ms.joinGlow, () => g.classList.remove("join"))
    }
  }

  const landSound = (e: MoveEvent, first: Place | undefined, width: number): void => {
    if (first === undefined) return
    clock.after(T(MOTION.ms.landSound), () => {
      const args = [valueOf(first.rings), scaleOf(first.rings), width] as const
      if (e.to.kind === "altar") sound.hold(...args)
      else sound.rest(...args)
    })
  }

  const playMove = async (e: MoveEvent): Promise<void> => {
    const state = e.after
    const g = measure(state)
    geo = g
    current = state
    drawSlots(state, g)
    const places = placesOf(state, g)
    const moved = new Set(e.ids)
    const anims: Promise<void>[] = []
    let k = 0
    for (const [id, p] of places) {
      const el = els.get(id)
      if (el === undefined) continue
      const old = pos.get(id) ?? p
      const from = dragAt.get(id) ?? old
      dragAt.delete(id)
      el.classList.remove("lifted", "selected")
      put(el, p)
      if (moved.has(id)) {
        const flip = drawn.get(id) !== keyOf(p.rings)
        anims.push(glide(el, from, p, { delay: T(k++ * MOTION.ms.glideStagger), flip, rings: p.rings, card: p.card }))
      } else if (old.x !== p.x || old.y !== p.y) anims.push(glide(el, old, p, "plain"))
      pos.set(id, p)
    }
    landSound(e, places.get(e.ids[0] ?? -1), state.width)
    await Promise.all(anims)
    if (e.to.kind === "col") glowJoin(state, e, e.to.i)
    markRuns(state)
  }

  const flash = (p: Place, g: Geo): void => {
    if (motion.reduced) return
    const f = document.createElement("div")
    f.className = "fold-flash"
    const at = `translate(${p.x + g.cw / 2}px, ${p.y + g.ch / 2}px)`
    f.style.transform = at
    layer.append(f)
    void motion
      .animate(f, [{ opacity: 0.9, transform: `${at} scale(0.2)` }, { opacity: 0, transform: `${at} scale(2.4)` }], {
        duration: MOTION.ms.flash,
        easing: MOTION.easing.flash,
      })
      .then(() => f.remove())
  }

  const drawIn = (el: HTMLElement, from: Point, target: Place, k: number): Promise<void> =>
    animate(
      el,
      [
        { transform: translate(from), filter: "brightness(1)" },
        { transform: `${translate(target)} scale(1.04)`, filter: "brightness(1.9)", offset: 0.7 },
        { transform: `${translate(target)} scale(0.86)`, filter: "brightness(2.6)", opacity: 0 },
      ],
      { duration: MOTION.ms.fold, delay: k * MOTION.ms.foldStagger, easing: MOTION.easing.fold, fill: "forwards" },
    )

  const playFold = async (e: FoldEvent): Promise<void> => {
    const state = e.after
    const g = measure(state)
    const places = placesOf(state, g)
    const target = places.get(e.card.id)
    if (target === undefined) return show(start ?? state, state, "instant")
    const ids = e.folded.map((c) => c.id)
    sound.fold(ids.map((id) => valueOf(drawnRings(id))), scaleOf(drawnRings(ids[1] ?? -1)), state.width)
    await Promise.all(ids.map((id, k) => (els.has(id) ? drawIn(els.get(id) ?? layer, pos.get(id) ?? target, target, k) : Promise.resolve())))
    for (const id of ids) {
      els.get(id)?.remove()
      forget(id)
    }
    const el = makeEl(e.card, target.rings)
    put(el, target)
    pos.set(e.card.id, target)
    flash(target, g)
    if (!motion.reduced) {
      void motion.animate(
        el,
        [
          { transform: `${translate(target)} scale(0.7)`, filter: "brightness(3)", opacity: 0.2 },
          { transform: `${translate(target)} scale(1.06)`, filter: "brightness(1.6)", opacity: 1, offset: 0.6 },
          { transform: `${translate(target)} scale(1)`, filter: "brightness(1)", opacity: 1 },
        ],
        { duration: MOTION.ms.foldBirth, easing: MOTION.easing.birth },
      )
      el.querySelector(`.sigil [data-ring="${e.ring}"]`)?.classList.add("just-folded")
    }
    // Everything else settles into its new place, and so do the empty places:
    // a fold can move the whole table (nine cards or fewer sit lower).
    current = state
    geo = g
    drawSlots(state, g)
    for (const [id, p] of places) {
      const other = els.get(id)
      if (id === e.card.id || other === undefined) continue
      const old = pos.get(id)
      put(other, p)
      if (old !== undefined && (old.x !== p.x || old.y !== p.y)) {
        void animate(other, [{ transform: translate(old) }, { transform: translate(p) }], { duration: MOTION.ms.foldShift, easing: MOTION.easing.settle })
      }
      pos.set(id, p)
    }
    markRuns(state)
    await wait(T(MOTION.ms.foldRest))
  }

  const playTurn = async (e: TurnEvent): Promise<void> => {
    const state = e.after
    const g = measure(state)
    geo = g
    current = state
    drawSlots(state, g)
    sound.turn()
    const places = placesOf(state, g)
    const cards = state.cols[e.col]?.cards ?? []
    const anims = cards.map((card, k) => {
      const el = els.get(card.id)
      const p = places.get(card.id)
      if (el === undefined || p === undefined) return Promise.resolve()
      const old = pos.get(card.id) ?? p
      put(el, p)
      pos.set(card.id, p)
      return glide(el, old, p, { delay: T(k * MOTION.ms.turnStagger), flip: true, rings: p.rings, card })
    })
    await Promise.all(anims)
    markRuns(state)
  }

  const playOne = (e: GameEvent): Promise<void> => {
    switch (e.type) {
      case "move":
        return playMove(e)
      case "fold":
        return playFold(e)
      case "turn":
        return playTurn(e)
      case "win":
        return Promise.resolve()
    }
  }

  const play = async (events: readonly GameEvent[]): Promise<void> => {
    busy = true
    try {
      for (const e of events) await playOne(e)
    } finally {
      busy = false
    }
  }

  // ---------- refusals: shown, never said ----------

  const tremble = (ids: readonly CardId[]): void => {
    for (const id of ids) {
      const el = els.get(id)
      const p = pos.get(id)
      if (el === undefined || p === undefined) continue
      const at = (dx: number): Keyframe => ({ transform: translate({ x: p.x + dx, y: p.y }) })
      void animate(el, [at(0), at(-4), at(4), at(-3), at(2), at(0)], { duration: MOTION.ms.tremble })
    }
  }

  const faultRings = (ids: readonly CardId[], rings: readonly RingIndex[]): void => {
    for (const id of ids) {
      const el = els.get(id)
      if (el === undefined) continue
      replay(el, "refused")
      for (const r of rings) for (const g of parts(el, `[data-ring="${r}"]`)) replay(g, "fault")
      clock.after(MOTION.ms.refused, () => {
        el.classList.remove("refused")
        for (const g of parts(el, ".fault")) g.classList.remove("fault")
      })
    }
  }

  const pulseSlots = (selector: string): void => {
    for (const s of parts(slots, selector)) replay(s, "pulse")
  }

  const refuse = (move: Move, refusal: Refusal, ids: readonly CardId[]): void => {
    sound.refuse()
    switch (refusal.why) {
      case "rest":
        return faultRings([refusal.card.id, refusal.onto.id], refusal.faults)
      case "burden":
        tremble(ids)
        return pulseSlots(".altar, .base")
      case "altar":
        return pulseSlots(`[data-altar="${move.to.i}"]`)
      case "none":
      case "same":
      case "run":
        return tremble(ids)
    }
  }

  // ---------- hands ----------

  const locate = (state: TableState, id: CardId): Place["where"] | undefined => {
    for (const [i, col] of state.cols.entries()) {
      const j = col.cards.findIndex((c) => c.id === id)
      if (j >= 0) return { kind: "col", i, j }
    }
    const a = state.altars.findIndex((c) => c !== "empty" && c.id === id)
    return a >= 0 ? { kind: "altar", i: a } : undefined
  }

  // The cards lifted when the player takes hold of card `id`.
  const grasp = (state: TableState, id: CardId): Grasp | undefined => {
    const at = locate(state, id)
    if (at === undefined) return undefined
    if (at.kind === "altar") return { from: { kind: "altar", i: at.i }, ids: [id], liftable: true }
    const col = state.cols[at.i]
    if (col === undefined) return undefined
    const n = col.cards.length - at.j
    return { from: { kind: "col", i: at.i, n }, ids: col.cards.slice(at.j).map((c) => c.id), liftable: n <= runLength(col) }
  }

  const clearSelection = (): void => {
    for (const id of selection?.ids ?? []) els.get(id)?.classList.remove("selected")
    selection = undefined
  }

  const select = (sel: Selection): void => {
    sound.lift()
    selection = sel
    for (const id of sel.ids) els.get(id)?.classList.add("selected")
  }

  const snapBack = (ids: readonly CardId[]): void => {
    for (const id of ids) {
      const el = els.get(id)
      const p = pos.get(id)
      if (el === undefined || p === undefined) continue
      const from = dragAt.get(id)
      dragAt.delete(id)
      el.classList.remove("lifted")
      put(el, p)
      if (from !== undefined) void animate(el, [{ transform: translate(from) }, { transform: translate(p) }], { duration: MOTION.ms.snapBack, easing: MOTION.easing.snap })
    }
  }

  const tryMove = async (sel: Selection, to: To): Promise<void> => {
    const ok = await deps.attempt({ from: sel.from, to }, sel.ids)
    if (!ok) snapBack(sel.ids)
  }

  // Where a card is drawn this moment. While it still glides to its place,
  // as it slides home after a refused drop, that is not its place; and a
  // player takes hold of the card they see.
  const drawnAt = (id: CardId): Point | undefined => {
    const el = els.get(id)
    const t = el === undefined ? "none" : getComputedStyle(el).transform
    if (t === "none") return pos.get(id)
    const m = new DOMMatrixReadOnly(t)
    return { x: m.m41, y: m.m42 }
  }

  const startOf = (id: CardId): readonly (readonly [CardId, Point])[] => {
    const at = drawnAt(id)
    return at === undefined ? [] : [[id, at]]
  }

  const onDown = (e: PointerEvent): void => {
    const el = closest(e, ".card")
    const id = el === undefined ? undefined : numberData(el, "id")
    if (busy || e.button > 0 || current === undefined || id === undefined) return
    sound.wake()
    const g = grasp(current, id)
    if (g === undefined) return
    e.preventDefault()
    drag = { ...g, starts: new Map(g.ids.flatMap(startOf)), x0: e.clientX, y0: e.clientY, pointer: e.pointerId, moved: false }
  }

  const hover = (target: To | undefined): void => {
    for (const s of parts(slots, ".hover")) s.classList.remove("hover")
    if (target === undefined) return
    const sel = target.kind === "altar" ? `[data-altar="${target.i}"]` : `.base[data-col="${target.i}"]`
    slots.querySelector(sel)?.classList.add("hover")
  }

  // Which column or altar the dragged cards are over, by the centre of the
  // first lifted card.
  const targetAt = (d: Drag): To | undefined => {
    const at = dragAt.get(d.ids[0] ?? -1)
    if (at === undefined || geo === undefined || current === undefined) return undefined
    const g = geo
    const cx = at.x + g.cw / 2
    const cy = at.y + g.ch / 2
    const over = (x: number): boolean => cx > x - g.gap / 2 && cx < x + g.cw + g.gap / 2
    if (cy < g.colY - g.cw * 0.2) {
      const i = current.altars.findIndex((_, k) => over(altarX(g, k)))
      return i >= 0 ? { kind: "altar", i } : undefined
    }
    const i = current.cols.findIndex((_, k) => over(colX(g, k)))
    return i >= 0 ? { kind: "col", i } : undefined
  }

  const lift = (d: Drag): void => {
    clearSelection()
    sound.lift()
    for (const [k, id] of d.ids.entries()) {
      const el = els.get(id)
      if (el === undefined) continue
      // A glide still running would hold the card away from the hand.
      for (const a of el.getAnimations()) a.cancel()
      el.classList.add("lifted")
      el.style.zIndex = String(2000 + k)
    }
  }

  const onMove = (e: PointerEvent): void => {
    const d = drag
    if (d === undefined || e.pointerId !== d.pointer) return
    const dx = e.clientX - d.x0
    const dy = e.clientY - d.y0
    if (!d.moved) {
      if (Math.hypot(dx, dy) < MOTION.dragStart) return
      if (!d.liftable) {
        tremble(d.ids.slice(0, 1))
        sound.refuse()
        drag = undefined
        return
      }
      drag = { ...d, moved: true }
      lift(d)
    }
    const tilt = Math.max(-MOTION.tilt.max, Math.min(MOTION.tilt.max, dx * MOTION.tilt.per))
    for (const id of d.ids) {
      const p = d.starts.get(id)
      const el = els.get(id)
      if (p === undefined || el === undefined) continue
      const at = { x: p.x + dx, y: p.y + dy }
      dragAt.set(id, at)
      el.style.transform = `${translate(at)} rotate(${tilt}deg)`
    }
    hover(targetAt(d))
  }

  // Two taps: the first takes hold, the second says where.
  const tap = (d: Drag): void => {
    const sel = selection
    if (sel !== undefined) {
      clearSelection()
      if (sel.ids.includes(d.ids[0] ?? -1) || current === undefined) return
      const at = locate(current, d.ids[0] ?? -1)
      if (at !== undefined) void tryMove(sel, at.kind === "altar" ? { kind: "altar", i: at.i } : { kind: "col", i: at.i })
      return
    }
    if (!d.liftable) {
      tremble(d.ids.slice(0, 1))
      sound.refuse()
      return
    }
    select({ from: d.from, ids: d.ids })
  }

  const sameSpot = (from: From, to: To): boolean => from.kind === to.kind && from.i === to.i

  const onUp = (e: PointerEvent): void => {
    const d = drag
    if (d === undefined || e.pointerId !== d.pointer) return
    drag = undefined
    hover(undefined)
    if (!d.moved) return tap(d)
    const to = targetAt(d)
    if (to === undefined || sameSpot(d.from, to)) return snapBack(d.ids)
    void tryMove(d, to)
  }

  const onCancel = (): void => {
    if (drag?.moved === true) snapBack(drag.ids)
    drag = undefined
    hover(undefined)
  }

  const onSlotClick = (e: MouseEvent): void => {
    const slot = closest(e, ".slot")
    const sel = selection
    if (busy || slot === undefined || sel === undefined) return
    const altar = numberData(slot, "altar")
    const col = numberData(slot, "col")
    const to: To | undefined = altar !== undefined ? { kind: "altar", i: altar } : col !== undefined ? { kind: "col", i: col } : undefined
    if (to === undefined) return
    clearSelection()
    void deps.attempt({ from: sel.from, to }, sel.ids)
  }

  // ---------- keys ----------

  // The ring that shows where the keyboard is: on the top card of a column,
  // or on an altar.
  function drawFocus(): void {
    const ring = slots.querySelector(".kb-ring")
    if (!(ring instanceof HTMLElement)) return
    const f = focus
    const s = current
    const g = geo
    ring.hidden = f === undefined || s === undefined || g === undefined
    if (f === undefined || s === undefined || g === undefined) return
    const n = s.cols[f.i]?.cards.length ?? 0
    const at = f.row === "altars" ? { x: altarX(g, f.i), y: g.topY } : { x: colX(g, f.i), y: g.colY + Math.max(0, n - 1) * (g.offsets[f.i] ?? 0) }
    ring.style.transform = translate(at)
  }

  // Enter takes the top card; Enter again on the same column takes one more
  // card of its run; Enter elsewhere lays them there.
  const keyAct = (f: Focus): void => {
    const s = current
    if (s === undefined) return
    const to: To = f.row === "altars" ? { kind: "altar", i: f.i } : { kind: "col", i: f.i }
    const sel = selection
    const here = sel !== undefined && sel.from.kind === to.kind && sel.from.i === f.i
    if (sel !== undefined && !here) {
      clearSelection()
      void tryMove(sel, to).then(drawFocus)
      return
    }
    if (f.row === "altars") {
      const card = s.altars[f.i]
      clearSelection()
      if (!here && card !== undefined && card !== "empty") select({ from: { kind: "altar", i: f.i }, ids: [card.id] })
      return
    }
    const col = s.cols[f.i]
    const n = here && sel.from.kind === "col" ? sel.from.n + 1 : 1
    clearSelection()
    if (col === undefined || col.cards.length === 0 || n > runLength(col)) {
      if (col === undefined || col.cards.length === 0 || !here) sound.refuse()
      return
    }
    select({ from: { kind: "col", i: f.i, n }, ids: col.cards.slice(col.cards.length - n).map((c) => c.id) })
  }

  const key = (k: string): boolean => {
    const s = current
    if (busy || s === undefined) return false
    const f = focus ?? { row: "cols", i: 0 }
    const moveTo = (row: Focus["row"], i: number): boolean => {
      const n = row === "altars" ? s.altars.length : s.cols.length
      const clamped = row === "altars" && f.row !== "altars" ? Math.min(i, n - 1) : i
      focus = { row, i: (clamped + n) % n }
      drawFocus()
      sound.wake()
      return true
    }
    switch (k) {
      case "ArrowLeft":
        return moveTo(f.row, f.i - 1)
      case "ArrowRight":
        return moveTo(f.row, f.i + 1)
      case "ArrowUp":
        return s.altars.length > 0 && moveTo("altars", f.i)
      case "ArrowDown":
        return moveTo("cols", f.i)
      case "Enter":
      case " ":
        if (focus === undefined) focus = f
        else keyAct(f)
        drawFocus()
        return true
      default:
        return false
    }
  }

  // ---------- help ----------

  const glimmer = (ids: readonly CardId[]): void => {
    for (const id of ids) {
      const el = els.get(id)
      if (el === undefined) continue
      replay(el, "glimmer")
      clock.after(MOTION.ms.glimmer, () => el.classList.remove("glimmer"))
    }
  }

  const liftedIds = (move: Move): readonly CardId[] => {
    const s = current
    if (s === undefined) return []
    if (move.from.kind === "altar") {
      const a = s.altars[move.from.i]
      return a === undefined || a === "empty" ? [] : [a.id]
    }
    const cards = s.cols[move.from.i]?.cards ?? []
    return cards.slice(cards.length - move.from.n).map((c) => c.id)
  }

  // A ghost of the lifted cards drifts to where they would go, then fades.
  const ghost = async (move: Move): Promise<void> => {
    const s = current
    const g = geo
    if (s === undefined || g === undefined) return
    const ids = liftedIds(move)
    const offset = g.offsets[move.to.i] ?? 0
    const step = offset > 0 ? offset : g.cw * LAYOUT.ghostFan
    const dest =
      move.to.kind === "altar"
        ? { x: altarX(g, move.to.i), y: g.topY }
        : { x: colX(g, move.to.i), y: g.colY + (s.cols[move.to.i]?.cards.length ?? 0) * step }
    const drift = ids.map((id, k) => {
      const src = els.get(id)
      const p = pos.get(id)
      if (src === undefined || p === undefined) return Promise.resolve()
      const c = src.cloneNode(true)
      if (!(c instanceof HTMLElement)) return Promise.resolve()
      c.classList.add("ghost")
      c.classList.remove("buried")
      c.style.zIndex = String(3000 + k)
      layer.append(c)
      const there = { x: dest.x, y: dest.y + k * g.cw * LAYOUT.ghostFan }
      return motion
        .animate(
          c,
          [
            { transform: translate(p), opacity: 0 },
            { transform: translate(p), opacity: 0.85, offset: 0.15 },
            { transform: translate(there), opacity: 0.85, offset: 0.7 },
            { transform: translate(there), opacity: 0 },
          ],
          { duration: MOTION.ms.ghost, easing: "ease-in-out" },
        )
        .then(() => c.remove())
    })
    glimmer(ids)
    await Promise.all(drift)
  }

  const setRosetta = (on: boolean): void => {
    rosetta = on
    root.classList.toggle("rosetta", on)
    if (start !== undefined && current !== undefined) show(start, current, "repaint")
  }

  layer.addEventListener("pointerdown", onDown)
  root.addEventListener("pointerdown", () => {
    if (focus === undefined) return
    focus = undefined
    drawFocus()
  })
  window.addEventListener("pointermove", onMove)
  window.addEventListener("pointerup", onUp)
  window.addEventListener("pointercancel", onCancel)
  slots.addEventListener("click", onSlotClick)
  window.addEventListener("resize", () => {
    if (start !== undefined && current !== undefined) show(start, current, "instant")
  })

  return { show, play, refuse, glimmer, ghost, liftedIds, setRosetta, clearSelection, key, busy: () => busy }
}
